import ActionSuccessToast from '../ActionSuccessToast'
import CommandSyncStatus from './CommandSyncStatus.jsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Keyboard, Plus, Radio, RotateCcw, Save, Search, Square, Trash2, X } from 'lucide-react'
import { useCommands } from '../../commands/CommandProvider.jsx'
import {
  DEFAULT_BINDINGS,
  COMMAND_PAGES,
  MACRO_POLICIES,
  formatSequence,
  normalizePlatform,
  strokeFromKeyboardEvent,
} from '../../commands/commandModel.js'
import { COMMAND_CUSTOMIZATION_SCHEMA_VERSION } from '../../lib/commandPreferenceStore.js'

function browserPlatform() {
  const raw = navigator.userAgentData?.platform || navigator.platform || 'mac'
  try {
    return normalizePlatform(raw)
  } catch {
    return /win/i.test(raw) ? 'windows' : /linux/i.test(raw) ? 'linux' : 'mac'
  }
}

function macroId(name) {
  const slug = String(name || 'macro').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48)
  return `${slug || 'macro'}-${Date.now()}`
}

function resultSummary(result) {
  if (!result) return null
  if (result.status === 'completed') return 'All steps are available in the current context.'
  if (result.status === 'interaction_barrier') {
    return `Stops safely before ${result.barrierCommandId}. Review and authentication remain manual.`
  }
  return `Blocked at step ${(result.stoppedBeforeStep ?? 0) + 1}: ${result.reason || result.status}`
}

export default function CommandSettings({ initialPageId = 'all', initialCommandId = null }) {
  const {
    commands,
    userBindings,
    macros,
    syncState,
    accountSync,
    invalidBindings,
    bindingConflicts,
    previewBinding,
    setBinding,
    clearBinding,
    resetBinding,
    resetAllBindings,
    saveMacro,
    deleteMacro,
    dryRunMacro,
    runMacro,
    recording,
    startRecording,
    stopRecording,
  } = useCommands()
  const platform = useMemo(browserPlatform, [])
  const [query, setQuery] = useState('')
  const [selectedPageId, setSelectedPageId] = useState(
    COMMAND_PAGES.some(page => page.id === initialPageId) ? initialPageId : 'all',
  )
  const [captureCommand, setCaptureCommand] = useState(null)
  const [capturedStrokes, setCapturedStrokes] = useState([])
  const [bindingMessage, setBindingMessage] = useState(null)
  const [bindingConfirmation, setBindingConfirmation] = useState(null)
  const captureRef = useRef(null)
  const handledDeepLinkRef = useRef(null)
  const [selectedShortcutCommandId, setSelectedShortcutCommandId] = useState('nav.position_workspace')
  const [macroName, setMacroName] = useState('')
  const [macroSteps, setMacroSteps] = useState([])
  const [selectedCommandId, setSelectedCommandId] = useState('nav.dashboard')
  const [macroMessage, setMacroMessage] = useState(null)

  const visibleCommands = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    const pageCommands = selectedPageId === 'all'
      ? commands
      : commands.filter(command => command.pageId === selectedPageId)
    if (!normalized) return pageCommands
    return pageCommands.filter(command => [command.label, command.description, command.category, command.id]
      .filter(Boolean).join(' ').toLowerCase().includes(normalized))
  }, [commands, query, selectedPageId])

  const macroCommands = useMemo(
    () => commands.filter(command => command.macroPolicy !== MACRO_POLICIES.FORBIDDEN),
    [commands],
  )

  const navigationCommands = useMemo(
    () => commands.filter(command => command.category === 'Navigation' && command.route),
    [commands],
  )

  const shortcutCommands = useMemo(() => (
    selectedPageId === 'all'
      ? navigationCommands
      : commands.filter(command => command.pageId === selectedPageId)
  ), [commands, navigationCommands, selectedPageId])

  useEffect(() => {
    if (shortcutCommands.some(command => command.id === selectedShortcutCommandId)) return
    setSelectedShortcutCommandId(shortcutCommands[0]?.id || '')
  }, [selectedShortcutCommandId, shortcutCommands])

  const beginBindingCapture = (command) => {
    if (!command) return
    setCaptureCommand(command)
    setCapturedStrokes([])
    setBindingMessage(null)
    setBindingConfirmation(null)
  }

  useEffect(() => {
    if (!initialCommandId || handledDeepLinkRef.current === initialCommandId) return
    const command = commands.find(item => item.id === initialCommandId)
    if (!command) return
    handledDeepLinkRef.current = initialCommandId
    if (COMMAND_PAGES.some(page => page.id === command.pageId)) setSelectedPageId(command.pageId)
    beginBindingCapture(command)
  }, [commands, initialCommandId])

  useEffect(() => {
    if (!captureCommand || bindingConfirmation) return undefined
    captureRef.current?.focus()
    const onKeyDown = (event) => {
      if (event.isComposing || event.repeat) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        setCaptureCommand(null)
        setCapturedStrokes([])
        return
      }
      if (event.key === 'Backspace') {
        event.preventDefault()
        event.stopImmediatePropagation()
        setCapturedStrokes(current => current.slice(0, -1))
        return
      }
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return
      event.preventDefault()
      event.stopImmediatePropagation()
      try {
        const stroke = strokeFromKeyboardEvent(event, platform)
        setCapturedStrokes(current => current.length >= 4 ? current : [...current, stroke])
      } catch {
        setBindingMessage({ type: 'error', text: 'That key cannot be used in an Erevis command.' })
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [bindingConfirmation, captureCommand, platform])

  const capturedPreview = useMemo(() => {
    if (!captureCommand || capturedStrokes.length === 0) return null
    return previewBinding(captureCommand.id, capturedStrokes)
  }, [captureCommand, capturedStrokes, previewBinding])

  const currentCommandView = (commandId) => (
    commands.find(command => command.id === commandId) || null
  )

  const hasActiveBinding = (command) => (command?.effectiveBinding || []).length > 0

  const applyCapturedBinding = async (command, sequence) => {
    const result = await setBinding(command.id, sequence)
    setBindingMessage({
      type: result.valid ? 'success' : 'error',
      text: result.valid
        ? `${command.label} is now ${formatSequence(sequence, platform)}${result.persisted ? '' : ' (account sync pending)'}`
        : result.reason,
    })
    if (result.valid) {
      setCaptureCommand(null)
      setCapturedStrokes([])
    }
  }

  const saveCapturedBinding = async () => {
    if (!captureCommand || !capturedPreview?.valid) return
    const latestCommand = currentCommandView(captureCommand.id) || captureCommand
    if (hasActiveBinding(latestCommand)) {
      setBindingConfirmation({ type: 'change', command: latestCommand, sequence: [...capturedStrokes] })
      return
    }
    await applyCapturedBinding(latestCommand, capturedStrokes)
  }

  const confirmBindingMutation = async () => {
    const pending = bindingConfirmation
    if (!pending) return
    if (accountSync && !accountSync.canEdit) { setBindingMessage({ type: 'error', text: 'Resolve account synchronization before changing this binding.' }); setBindingConfirmation(null); return }
    setBindingConfirmation(null)
    if (pending.type === 'clear') {
      const persisted = await clearBinding(pending.command.id)
      setBindingMessage({
        type: 'success',
        text: `${pending.command.label} is now unbound.${persisted ? '' : ' Account sync pending. Check the sync status above.'}`,
      })
      return
    }
    await applyCapturedBinding(pending.command, pending.sequence)
  }

  useEffect(() => {
    if (!bindingConfirmation) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        setBindingConfirmation(null)
        return
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopImmediatePropagation()
        confirmBindingMutation()
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [bindingConfirmation]) // eslint-disable-line react-hooks/exhaustive-deps

  const addMacroStep = () => {
    if (!selectedCommandId) return
    setMacroSteps(current => [...current, { type: 'command', commandId: selectedCommandId }])
  }

  const saveBuilderMacro = async () => {
    const result = await saveMacro({
      id: macroId(macroName),
      name: macroName.trim(),
      schemaVersion: COMMAND_CUSTOMIZATION_SCHEMA_VERSION,
      enabled: true,
      steps: macroSteps,
    })
    setMacroMessage(result.ok
      ? { type: 'success', text: `Saved ${result.macro.name}${result.persisted ? '' : '; account sync pending'}` }
      : { type: 'error', text: 'Add a name and at least one allowed Erevis command.' })
    if (result.ok) {
      setMacroName('')
      setMacroSteps([])
    }
  }

  const finishRecording = async () => {
    const draft = stopRecording()
    if (!draft || draft.steps.length === 0) {
      setMacroMessage({ type: 'error', text: 'No semantic commands were recorded.' })
      return
    }
    const result = await saveMacro(draft)
    setMacroMessage(result.ok
      ? { type: 'success', text: (result.persisted ? `Saved recorded macro ${draft.name}.` : `Recorded macro ${draft.name}: account sync pending.`) }
      : { type: 'error', text: 'The recorded sequence did not pass macro policy validation.' })
  }

  return (
    <section id="commands-navigation" className="overflow-hidden rounded-xl border border-border bg-canvas">
      <header className="border-b border-border px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Keyboard size={15} className="text-inkMuted" />
              <h2 className="text-sm font-black uppercase tracking-wide text-ink">Commands &amp; Navigation</h2>
            </div>
            <p className="mt-1 max-w-xl font-mono text-[10px] leading-relaxed text-inkMuted">
              Customize navigation and UI commands. Command scope and trading safety remain code-owned.
            </p>
          </div>
          <span className={`rounded-md border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${
            syncState === 'synced' ? 'border-forest/40 bg-forest/10 text-forest' : 'border-border bg-canvas2 text-inkMuted'
          }`}>
            {(accountSync?.shared ? { loading: 'Loading shared commands', saving: 'Saving commands', synced: 'Shared commands synced', draft: 'Staging draft', 'waiting-source': 'Waiting for Production', offline: 'Shared sync unavailable', conflict: 'Settings conflict — review required', 'signed-out': 'Sign in to sync' } : { loading: 'Loading account settings', saving: 'Saving to account', synced: 'Account synced', offline: 'Account unavailable — retry pending', conflict: 'Device conflict — review required', invalid: 'Invalid settings — review required', 'signed-out': 'Sign in to save' })[syncState] || 'Account unavailable'}
          </span>
        </div>
      </header>

      <CommandSyncStatus sync={accountSync} userBindings={userBindings} macros={macros} />
      <fieldset disabled={Boolean(accountSync && !accountSync.canEdit)} className="space-y-8 px-6 py-5">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink">Key bindings</h3>
              <p className="mt-0.5 font-mono text-[10px] text-inkMuted">Up to four keys. Exact and prefix conflicts are blocked.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (confirm('Reset every custom command binding to the Erevis defaults?')) resetAllBindings()
              }}
              disabled={userBindings.length === 0}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold text-inkMuted hover:border-ink/30 hover:text-ink disabled:opacity-40"
            >
              <RotateCcw size={12} /> Reset all
            </button>
          </div>

          <label className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-canvas2 px-3 focus-within:ring-2 focus-within:ring-ink/20">
            <Search size={14} className="text-inkMuted" />
            <span className="sr-only">Search commands</span>
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search commands…"
              className="h-10 min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-inkDim"
            />
          </label>

          <div className="mt-3 flex flex-wrap gap-2" aria-label="Command pages">
            {COMMAND_PAGES.map(page => {
              const active = selectedPageId === page.id
              return (
                <button
                  key={page.id}
                  type="button"
                  aria-label={`${page.label} commands`}
                  aria-pressed={active}
                  onClick={() => {
                    setSelectedPageId(page.id)
                    setQuery('')
                  }}
                  className={`rounded-md border px-3 py-2 text-left transition-colors ${
                    active
                      ? 'border-ink/30 bg-canvas2 text-ink'
                      : 'border-border bg-canvas text-inkMuted hover:border-ink/20 hover:text-ink'
                  }`}
                >
                  <span className="block text-xs font-semibold">{page.label}</span>
                  <span className="mt-0.5 block font-mono text-[9px] text-inkDim">{page.description}</span>
                </button>
              )
            })}
          </div>

          <div className="mt-3 rounded-lg border border-border bg-canvas2/40 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="min-w-0 flex-1">
                <span className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Shortcut destination</span>
                <select
                  aria-label="Shortcut destination"
                  value={selectedShortcutCommandId}
                  onChange={event => setSelectedShortcutCommandId(event.target.value)}
                  className="mt-1.5 w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-ink/40"
                >
                  {shortcutCommands.map(command => (
                    <option key={command.id} value={command.id}>{command.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => beginBindingCapture(shortcutCommands.find(command => command.id === selectedShortcutCommandId))}
                disabled={shortcutCommands.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-forest/40 bg-forest/10 px-3 py-2 text-xs font-semibold text-forest disabled:opacity-40"
              >
                <Plus size={12} /> Create key command
              </button>
            </div>
            <p className="mt-2 font-mono text-[9px] leading-relaxed text-inkDim">
              Choose an approved Erevis destination, then record your own 1–4 key sequence. Arbitrary URLs and broker actions are not available here.
            </p>
          </div>

          <div className="mt-3 divide-y divide-border overflow-hidden rounded-lg border border-border">
            {visibleCommands.map(command => {
              const userBinding = userBindings.find(row => row.commandId === command.id)
              const customized = Boolean(userBinding)
              const cleared = userBinding?.enabled === false
              const defaults = DEFAULT_BINDINGS.filter(binding => binding.commandId === command.id)
                .map(binding => formatSequence(binding.sequence, platform))
              return (
                <div key={command.id} className="flex flex-col gap-3 bg-canvas px-3 py-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-ink">{command.label}</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-inkDim">{command.scopeLabel}</span>
                      {customized && <span className={`rounded border px-1.5 py-0.5 font-mono text-[8px] uppercase ${
                        cleared
                          ? 'border-border bg-canvas2 text-inkMuted'
                          : 'border-forest/30 bg-forest/10 text-forest'
                      }`}>{cleared ? 'Unbound' : 'Custom'}</span>}
                    </div>
                    <div className="mt-0.5 text-xs text-inkMuted">{command.description}</div>
                    <div className="mt-1 font-mono text-[9px] text-inkDim">Default: {defaults.join(' · ') || 'Unbound'} · Safety: {command.risk}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(command.effectiveBinding || []).map(binding => (
                      <kbd key={binding} className="rounded border border-border bg-canvas2 px-2 py-1 font-mono text-[10px] text-ink">{binding}</kbd>
                    ))}
                    <button
                      type="button"
                      aria-label={`Change ${command.label} key command`}
                      onClick={() => beginBindingCapture(command)}
                      className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-inkMuted hover:border-ink/30 hover:text-ink"
                    >
                      Change
                    </button>
                    {(command.effectiveBinding || []).length > 0 && (
                      <button
                        type="button"
                        aria-label={`Clear ${command.label} key command`}
                        onClick={() => setBindingConfirmation({ type: 'clear', command })}
                        className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-inkMuted hover:border-ember/30 hover:text-ember"
                      >
                        Clear
                      </button>
                    )}
                    {customized && (
                      <button type="button" aria-label={`Reset ${command.label}`} onClick={() => resetBinding(command.id)} className="rounded-md p-2 text-inkMuted hover:bg-canvas2 hover:text-ink">
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <ActionSuccessToast message={bindingMessage?.type === 'success' ? bindingMessage.text : null} onDismiss={() => setBindingMessage(null)} />
          {((bindingMessage && bindingMessage.type !== 'success') || invalidBindings.length > 0 || bindingConflicts.length > 0) && (
            <div className={`mt-3 font-mono text-[10px] ${bindingMessage?.type === 'error' || invalidBindings.length || bindingConflicts.length ? 'text-ember' : 'text-forest'}`}>
              {bindingMessage?.text || `${invalidBindings.length} invalid and ${bindingConflicts.length} conflicting binding records are quarantined.`}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-7">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink">Safe macros</h3>
              <p className="mt-0.5 max-w-xl font-mono text-[10px] leading-relaxed text-inkMuted">
                Macros contain registered Erevis commands only. They stop before confirmation, WebAuthn, or OMS.
              </p>
            </div>
            {!recording ? (
              <button type="button" onClick={() => startRecording(macroName.trim() || 'Recorded macro')} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold text-ink hover:border-ink/30">
                <Radio size={12} /> Record semantic commands
              </button>
            ) : (
              <button type="button" onClick={finishRecording} className="inline-flex items-center gap-2 rounded-md border border-ember/40 bg-ember/10 px-3 py-2 text-xs font-semibold text-ember">
                <Square size={11} /> Stop &amp; save ({recording.steps.length})
              </button>
            )}
          </div>

          {recording && (
            <div className="mt-3 rounded-lg border border-ember/30 bg-ember/5 px-3 py-2 font-mono text-[10px] text-inkMuted">
              Recording registered commands as semantic IDs. Navigate and run commands normally, then return here to stop.
              {recording.stoppedAtBarrier && <span className="ml-2 text-ember">Interaction barrier reached; recording will not continue through it.</span>}
            </div>
          )}

          <div className="mt-4 rounded-lg border border-border bg-canvas2/40 p-3">
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto]">
              <input
                aria-label="Macro name"
                value={macroName}
                onChange={event => setMacroName(event.target.value)}
                placeholder="Macro name"
                className="rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-ink/40"
              />
              <select aria-label="Macro command" value={selectedCommandId} onChange={event => setSelectedCommandId(event.target.value)} className="rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-ink/40">
                {macroCommands.map(command => <option key={command.id} value={command.id}>{command.label}</option>)}
              </select>
              <button type="button" onClick={addMacroStep} className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-canvas px-3 py-2 text-xs font-semibold text-ink hover:border-ink/30">
                <Plus size={12} /> Add
              </button>
            </div>

            <div className="mt-3 space-y-1.5">
              {macroSteps.map((step, index) => {
                const command = commands.find(item => item.id === step.commandId)
                return (
                  <div key={`${step.commandId}-${index}`} className="flex items-center gap-3 rounded-md border border-border bg-canvas px-3 py-2">
                    <span className="font-mono text-[9px] text-inkDim">{index + 1}</span>
                    <span className="min-w-0 flex-1 text-xs font-semibold text-ink">{command?.label || step.commandId}</span>
                    {command?.macroPolicy === MACRO_POLICIES.INTERACTION_BARRIER && <span className="font-mono text-[8px] uppercase text-ember">Stops here</span>}
                    <button type="button" aria-label={`Remove step ${index + 1}`} onClick={() => setMacroSteps(current => current.filter((_, itemIndex) => itemIndex !== index))} className="text-inkMuted hover:text-ember">
                      <X size={12} />
                    </button>
                  </div>
                )
              })}
              {macroSteps.length === 0 && <div className="py-3 text-center font-mono text-[10px] text-inkDim">Add registered commands to build a macro.</div>}
            </div>

            <button type="button" onClick={saveBuilderMacro} disabled={!macroName.trim() || macroSteps.length === 0} className="mt-3 inline-flex items-center gap-2 rounded-md border border-forest/40 bg-forest/10 px-3 py-2 text-xs font-semibold text-forest disabled:opacity-40">
              <Save size={12} /> Save macro
            </button>
          </div>

          <div className="mt-3 space-y-2">
            {macros.map(macro => (
              <div key={macro.id} className="rounded-lg border border-border bg-canvas px-3 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-ink">{macro.name}</div>
                    <div className="mt-0.5 font-mono text-[9px] text-inkMuted">{macro.steps.length} semantic steps · v{macro.schemaVersion}</div>
                  </div>
                  <button type="button" onClick={async () => setMacroMessage({ type: 'info', text: resultSummary(await dryRunMacro(macro)) })} className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-inkMuted hover:text-ink">Dry run</button>
                  <button type="button" onClick={async () => setMacroMessage({ type: 'info', text: resultSummary(await runMacro(macro)) })} className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-inkMuted hover:text-ink">Run</button>
                  <button type="button" aria-label={`Delete ${macro.name}`} onClick={() => deleteMacro(macro.id)} className="rounded-md p-2 text-inkMuted hover:bg-ember/10 hover:text-ember"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
            {macros.length === 0 && <div className="rounded-lg border border-dashed border-border py-4 text-center font-mono text-[10px] text-inkDim">No macros yet.</div>}
          </div>

          <ActionSuccessToast message={macroMessage?.type === 'success' ? macroMessage.text : null} onDismiss={() => setMacroMessage(null)} />
          {macroMessage && macroMessage.type !== 'success' && <div className={`mt-3 font-mono text-[10px] ${macroMessage.type === 'error' ? 'text-ember' : macroMessage.type === 'success' ? 'text-forest' : 'text-inkMuted'}`}>{macroMessage.text}</div>}
        </div>
      </fieldset>

      {captureCommand && !bindingConfirmation && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-ink/30 px-4 backdrop-blur-sm">
          <div ref={captureRef} tabIndex={-1} data-command-shield="true" role="dialog" aria-modal="true" aria-label={`Change ${captureCommand.label} binding`} className="w-full max-w-md rounded-xl border border-border bg-canvas p-5 shadow-2xl outline-none">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">New key command</div>
                <h3 className="mt-1 text-base font-semibold text-ink">{captureCommand.label}</h3>
              </div>
              <button type="button" aria-label="Cancel binding capture" onClick={() => setCaptureCommand(null)} className="rounded-md p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink"><X size={15} /></button>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-inkMuted">Press one chord or a sequence of up to four keys. Backspace removes the last key; hover any key to remove it; Esc cancels.</p>
            <div className="mt-4 flex min-h-14 items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-canvas2/50 px-3">
              {capturedStrokes.length === 0
                ? <span className="font-mono text-xs text-inkDim">Waiting for keys…</span>
                : capturedStrokes.map((stroke, index) => {
                  const label = formatSequence([stroke], platform)
                  return (
                    <span key={index} className="group relative inline-flex items-center rounded border border-border bg-canvas pl-2 pr-1 py-1 font-mono text-xs text-ink">
                      <kbd>{label}</kbd>
                      <button
                        type="button"
                        aria-label={`Remove ${label} from key command`}
                        onClick={() => setCapturedStrokes(current => current.filter((_, itemIndex) => itemIndex !== index))}
                        className="ml-1 rounded p-0.5 text-inkDim opacity-0 transition-opacity hover:bg-canvas2 hover:text-ember focus:opacity-100 group-hover:opacity-100"
                      >
                        <X size={10} />
                      </button>
                    </span>
                  )
                })}
            </div>
            {capturedPreview && !capturedPreview.valid && <div className="mt-2 font-mono text-[10px] text-ember">{capturedPreview.reason}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setCaptureCommand(null)} className="rounded-md border border-border px-3 py-2 text-xs font-semibold text-inkMuted hover:text-ink">Cancel</button>
              <button type="button" onClick={saveCapturedBinding} disabled={!capturedPreview?.valid} className="rounded-md border border-forest/40 bg-forest/10 px-3 py-2 text-xs font-semibold text-forest disabled:opacity-40">Save binding</button>
            </div>
          </div>
        </div>
      )}

      {bindingConfirmation && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-ink/30 px-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={bindingConfirmation.type === 'clear'
              ? `Confirm clear ${bindingConfirmation.command.label}`
              : `Confirm change to ${bindingConfirmation.command.label}`}
            data-command-shield="true"
            className="w-full max-w-sm rounded-xl border border-border bg-canvas p-5 shadow-2xl"
          >
            <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Confirm key command</div>
            <h3 className="mt-1 text-base font-semibold text-ink">
              {bindingConfirmation.type === 'clear' ? 'Clear this key command?' : 'Replace this key command?'}
            </h3>
            <p className="mt-3 text-xs leading-relaxed text-inkMuted">
              {bindingConfirmation.type === 'clear'
                ? `${bindingConfirmation.command.label} will no longer respond to its current keys.`
                : `${(bindingConfirmation.command.effectiveBinding || []).join(' · ')} will be replaced by ${formatSequence(bindingConfirmation.sequence, platform)}.`}
            </p>
            <p className="mt-2 font-mono text-[9px] text-inkDim">Enter confirms · Esc cancels</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setBindingConfirmation(null)} className="rounded-md border border-border px-3 py-2 text-xs font-semibold text-inkMuted hover:text-ink">Cancel</button>
              <button type="button" autoFocus onClick={confirmBindingMutation} className="rounded-md border border-ember/40 bg-ember/10 px-3 py-2 text-xs font-semibold text-ember">
                {bindingConfirmation.type === 'clear' ? 'Clear binding' : 'Confirm change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
